import { RE2JS } from 're2js';

import {
  endOfMatchMark,
  resolveOverlappingRanges,
  startOfMatchMark,
} from '@neptune/shared/common-util';

import { searchModelConverter } from './search-model-converter';
import { SearchQueryTermWithoutPositions } from './types';

type MatchRange = {
  start: number;
  end: number;
};

const getMatchRangesForMatcher = (text: string, compiled: RE2JS): MatchRange[] => {
  if (text.length === 0) {
    return [];
  }

  const emptyMatcher = compiled.matcher('');

  if (emptyMatcher.find()) {
    const emptyStart = +emptyMatcher.start(0);
    const emptyEnd = +emptyMatcher.end(0);

    if (emptyStart === emptyEnd) {
      return [];
    }
  }

  const matcher = compiled.matcher(text);
  const ranges: MatchRange[] = [];
  let nextIndex = 0;

  while (nextIndex <= text.length && matcher.find(nextIndex)) {
    const start = +matcher.start(0);
    const end = +matcher.end(0);

    if (start === end) {
      nextIndex = end + 1;
      continue;
    }

    const nextRange = { start, end };
    const lastRange = ranges[ranges.length - 1];

    if (lastRange && nextRange.start <= lastRange.end) {
      lastRange.end = Math.max(lastRange.end, nextRange.end);
    } else {
      ranges.push(nextRange);
    }

    nextIndex = end;
  }

  return ranges;
};

const getMatchRangesForCompiled = (text: string, compiled: RE2JS[]): MatchRange[] => {
  if (compiled.length === 0) {
    return [];
  }

  const ranges: MatchRange[] = [];

  for (const matcher of compiled) {
    const matcherRanges = getMatchRangesForMatcher(text, matcher);

    if (matcherRanges.length === 0) {
      return [];
    }

    ranges.push(...matcherRanges);
  }

  return ranges;
};

const getMustMatchRegexGroupsFromQuery = (terms: SearchQueryTermWithoutPositions[]): string[][] => {
  if (terms.length === 0) {
    return [];
  }

  const { mustMatchAny } = searchModelConverter.fromSearchQueryToAttributeNameFilter(terms);
  const groups = mustMatchAny ?? [];

  return groups
    .map((group) =>
      (group.mustMatchRegexes ?? []).filter(
        (value): value is string => typeof value === 'string' && value.trim().length > 0,
      ),
    )
    .filter((group) => group.length > 0);
};

const compileRegexGroups = (regexGroups: string[][]): RE2JS[][] =>
  regexGroups.map((regexes) => regexes.map((regex) => RE2JS.compile(regex)));

const buildMatchMarkedTextFromCompiled = (
  text: string,
  compiled: RE2JS[][],
): string | undefined => {
  if (text.length === 0) {
    return;
  }

  if (compiled.length === 0) {
    return;
  }

  const matchRanges: MatchRange[] = [];

  for (const compiledPattern of compiled) {
    matchRanges.push(...getMatchRangesForCompiled(text, compiledPattern));
  }

  const mergedRanges = resolveOverlappingRanges(matchRanges);

  if (mergedRanges.length === 0) {
    return;
  }

  let cursor = 0;
  let result = '';

  for (const range of mergedRanges) {
    if (range.start > cursor) {
      result += text.slice(cursor, range.start);
    }

    result += `${startOfMatchMark}${text.slice(range.start, range.end)}${endOfMatchMark}`;
    cursor = range.end;
  }

  if (cursor < text.length) {
    result += text.slice(cursor);
  }

  return result;
};

export const createRegexQueryMatchHighlighter = (query: SearchQueryTermWithoutPositions[]) => {
  try {
    const compiledGroups = compileRegexGroups(getMustMatchRegexGroupsFromQuery(query));

    return (text: string) => buildMatchMarkedTextFromCompiled(text, compiledGroups);
  } catch {
    return (_text: string) => undefined;
  }
};
